import * as ToastPrimitives from '@radix-ui/react-toast'
import { type VariantProps, cva } from 'class-variance-authority'
import { CheckCircle2, Info, LoaderCircle, XCircle, XIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { buttonVariants } from '@/components/lemmala-design-system/button/button-variants'

export type ToastVariant = 'default' | 'destructive' | 'success' | 'loading'

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 z-max flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-fit',
      className,
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full group bg-main-background-card pointer-events-auto relative flex w-full md:w-[335px] items-start gap-3 overflow-hidden rounded-lg p-1 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[swipe=cancel]:transition-none data-[swipe=end]:transition-none',
  {
    variants: {
      variant: {
        default: '',
        destructive: '',
        success: '',
        loading: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

interface ToastProps
  extends
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root>,
    VariantProps<typeof toastVariants> {
  icon?: boolean
  omitIcon?: boolean
  action?: ToastActionElement
  description?: React.ReactNode
}

const Toast = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Root>, ToastProps>(
  (
    {
      className,
      variant,
      icon,
      omitIcon = false,
      action: _action,
      description: _description,
      children,
      ...props
    },
    ref,
  ) => {
    const hasDefaultIcon =
      variant === 'success' || variant === 'destructive' || variant === 'loading'
    const shouldShowIcon = !omitIcon && (icon ?? hasDefaultIcon)

    return (
      <ToastPrimitives.Root
        ref={ref}
        className={cn(toastVariants({ variant }), className)}
        {...props}
      >
        {/* Color indicator strip */}
        <div
          data-testid='toast-color-indicator'
          className={cn(
            'absolute left-0 top-0 h-full w-1.5',
            variant === 'destructive' && 'bg-main-background-danger',
            variant === 'success' && 'bg-main-background-success',
            variant === 'loading' && 'bg-transparent',
          )}
        />

        {/* Inset border */}
        <div className='pointer-events-none absolute inset-0 rounded-lg border border-main-border-default' />

        <div className='w-full'>
          <div className='flex justify-end'>
            <ToastClose className='size-6 p-0' />
          </div>
          <div data-testid='toast-content-wrapper' className='flex items-start px-6 pb-5'>
            {shouldShowIcon && (
              <div className='mr-2'>
                <ToastIcon variant={variant || 'default'} />
              </div>
            )}
            {children}
          </div>
        </div>
      </ToastPrimitives.Root>
    )
  },
)
Toast.displayName = ToastPrimitives.Root.displayName

const ToastIcon = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'> & {
    variant?: ToastVariant | null
  }
>(({ className, variant = 'default', ...props }, ref) => {
  const getIcon = () => {
    switch (variant) {
      case 'success':
        return <CheckCircle2 className='size-5 text-main-background-success' />
      case 'loading':
        return <LoaderCircle className='size-5 animate-spin text-main-foreground-tertiary' />
      case 'destructive':
        return <XCircle className='size-5 text-main-background-danger' />
      case 'default':
      case null:
      case undefined:
        return <Info className='size-5 text-main-foreground-accent' />
      default:
        return <Info className='size-5 text-main-foreground-accent' />
    }
  }

  return (
    <div
      ref={ref}
      className={cn('flex shrink-0 items-start', className)}
      data-testid='toast-icon'
      {...props}
    >
      {getIcon()}
    </div>
  )
})
ToastIcon.displayName = 'ToastIcon'

const ToastContent = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col', className)}
      data-testid='toast-content'
      {...props}
    />
  ),
)
ToastContent.displayName = 'ToastContent'

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(buttonVariants({ variant: 'secondary' }), className)}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(buttonVariants({ variant: 'ghost' }), className)}
    toast-close=''
    data-testid='toast-close-button'
    {...props}
  >
    <XIcon className='size-4' />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn('text-heading-xxsmall', className)} {...props} />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('text-body-default', className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastIcon,
  ToastContent,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}
